// Setup › Finance › FX rates (`/c/{slug}/setup/finance/fx`; 11 §Money representation, M3-04
// `fxRates.*`): the rate of a currency pair for a month. Every amount in another currency than
// the reporting one is converted once, with its own month's rate, and never with a live rate;
// a month without the rate it needs makes the finance views answer "no FX rate" until it is set
// here. Setting a pair's rate again replaces it for that month. Managers. One call on load.
import {
  Button,
  EmptyState,
  Input,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeftRightIcon, PlusIcon } from "lucide-react";
import { useId, useMemo, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { formatDateTime } from "../format";
import { LoadError, Loading, LoadMore } from "../screen-parts";
import {
  type Currency,
  CurrencySelect,
  FinancePage,
  FormDialog,
  FormField,
  MonthSelect,
  monthKeyOf,
  monthLabel,
  monthsAround,
} from "./finance-parts";
import { financeKeys, fxQuery } from "./finance-queries";

type Rate = Awaited<ReturnType<Api["client"]["fxRates"]["list"]>>["data"][number];

export function FxScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const [editing, setEditing] = useState<Rate | "new" | null>(null);
  const rates = useInfiniteQuery(fxQuery(api, company.id));
  const rows = rates.data?.pages.flatMap((p) => p.data) ?? [];
  return (
    <FinancePage
      company={company}
      where="setup"
      title="FX rates"
      icon={ArrowLeftRightIcon}
      actions={
        <Button size="sm" onClick={() => setEditing("new")}>
          <PlusIcon aria-hidden="true" />
          Set a rate
        </Button>
      }
    >
      {rates.isPending ? (
        <Loading label="Loading FX rates" rows={3} />
      ) : rates.isError ? (
        <LoadError what="FX rates" error={rates.error} />
      ) : rows.length === 0 ? (
        <EmptyState
          variant="card"
          icon={ArrowLeftRightIcon}
          title="No FX rates yet"
          description="Only needed when a cost, a pay or a revenue row is in another currency than the company reports in."
        />
      ) : (
        <div className="flex min-w-0 flex-col gap-3">
          <Table data-slot="fx-rates">
            <TableCaption>One unit of the first currency in the second, per month.</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">Month</TableHead>
                <TableHead scope="col">Pair</TableHead>
                <TableHead scope="col" className="text-right">
                  Rate
                </TableHead>
                <TableHead scope="col" className="max-md:hidden">
                  Source
                </TableHead>
                <TableHead scope="col" className="max-md:hidden">
                  Set
                </TableHead>
                <TableHead scope="col">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => {
                const pair = `${r.from_currency}-${r.to_currency}`;
                return (
                  <TableRow key={`${r.period_key}-${pair}`} data-fx={`${r.period_key}:${pair}`}>
                    <TableHead scope="row" className="font-normal">
                      {monthLabel(r.period_key)}
                    </TableHead>
                    <TableCell>
                      {r.from_currency} → {r.to_currency}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{r.rate}</TableCell>
                    <TableCell className="text-muted-foreground max-md:hidden">
                      {r.source ?? "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground max-md:hidden">
                      {formatDateTime(r.updated_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => setEditing(r)}>
                        Change
                        <span className="sr-only">
                          {" "}
                          {pair} for {monthLabel(r.period_key)}
                        </span>
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <LoadMore
            what="rates"
            hasMore={rates.hasNextPage}
            loading={rates.isFetchingNextPage}
            onMore={() => void rates.fetchNextPage()}
          />
        </div>
      )}
      {editing ? (
        <RateDialog
          api={api}
          company={company}
          row={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </FinancePage>
  );
}

function RateDialog({
  api,
  company,
  row,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  row: Rate | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const current = monthKeyOf(new Date());
  const months = useMemo(() => monthsAround(current, 24, 1), [current]);
  const [month, setMonth] = useState(row?.period_key ?? current);
  const [from, setFrom] = useState<Currency>((row?.from_currency as Currency) ?? "EUR");
  const [to, setTo] = useState<Currency>((row?.to_currency as Currency) ?? "USD");
  const [rate, setRate] = useState(row?.rate ?? "");
  const [source, setSource] = useState(row?.source ?? "");
  const ids = { month: useId(), from: useId(), to: useId(), rate: useId(), source: useId() };
  const rateOk = /^\d{1,12}(\.\d{1,6})?$/.test(rate.trim()) && Number(rate) > 0;
  const put = useMutation({
    mutationFn: () =>
      api.client.fxRates.put({
        params: { co: company.id, period: month, pair: `${from}-${to}` },
        body: { rate: rate.trim(), ...(source.trim() ? { source: source.trim() } : {}) },
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: financeKeys.all(company.id) });
      onClose();
    },
  });
  return (
    <FormDialog
      open
      onOpenChange={(o) => (o ? null : onClose())}
      title={row ? "Change the rate" : "Set a rate"}
      description="How much one unit of the first currency is worth in the second, for every amount of that month."
      submitLabel="Save rate"
      pending={put.isPending}
      error={put.error}
      canSubmit={rateOk && from !== to}
      onSubmit={() => put.mutate()}
    >
      <div className="grid gap-4 sm:grid-cols-3">
        <FormField id={ids.month} label="Month">
          <MonthSelect id={ids.month} value={month} onChange={setMonth} months={months} />
        </FormField>
        <FormField id={ids.from} label="From">
          <CurrencySelect id={ids.from} value={from} onChange={setFrom} />
        </FormField>
        <FormField
          id={ids.to}
          label="To"
          error={from === to ? "Pick two different currencies." : null}
        >
          <CurrencySelect id={ids.to} value={to} onChange={setTo} />
        </FormField>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          id={ids.rate}
          label="Rate"
          error={rate !== "" && !rateOk ? "A positive number with up to 6 decimals." : null}
        >
          <Input
            id={ids.rate}
            inputMode="decimal"
            required
            value={rate}
            onChange={(e) => setRate(e.target.value)}
          />
        </FormField>
        <FormField id={ids.source} label="Source" hint="Where the rate comes from (optional).">
          <Input
            id={ids.source}
            maxLength={200}
            value={source}
            onChange={(e) => setSource(e.target.value)}
          />
        </FormField>
      </div>
    </FormDialog>
  );
}
